"use client";

import React from "react";
import { APP_URL } from "@/src/core/config/app";
import { jsonLdService } from "@/src/shared/services/jsonLd";

type OgImage = {
  url: string; // absolute or path; will be made absolute
  width?: number;
  height?: number;
  alt?: string;
  type?: string; // e.g. "image/jpeg"
};

interface ISEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  url?: string;
  images?: OgImage[]; // FIRST image is used by WhatsApp/Facebook
  robots?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  siteName?: string;
  siteUrl?: string; // e.g. https://travela.xyz
  locale?: string;
  twitterHandle?: string;
  defaultKeywords?: string[];
  fbAppId?: string;
  ogType?: "website" | "article" | "product" | string;
  updatedTimeISO?: string;
  ogTitle?: string; // distinct OG/Twitter title (falls back to `title`)
  ogDescription?: string; // distinct OG/Twitter description (falls back to `description`)
  articlePublishedTime?: string; // og article:published_time (ISO)
  articleModifiedTime?: string; // og article:modified_time (ISO)
}

const makeAbsolute = (maybePath: string, siteUrl: string): string => {
  try {
    return new URL(maybePath).toString(); // already absolute
  } catch {
    const base = siteUrl.replace(/\/$/, "");
    const path = maybePath.startsWith("/") ? maybePath : `/${maybePath}`;
    return `${base}${path}`;
  }
};

const inferMime = (u?: string) =>
  u?.match(/\.(jpe?g)$/i)
    ? "image/jpeg"
    : u?.match(/\.png$/i)
      ? "image/png"
      : u?.match(/\.webp$/i)
        ? "image/webp"
        : undefined;

const listingImages = (images: OgImage[] = []): OgImage[] => {
  if (!images || images.length === 0) {
    return [
      {
        url: `${APP_URL}/images/small-logo.png`,
        width: 800,
        height: 600,
        alt: "Travela - Book Hotels, Apartments & Vacation Rentals in Bangladesh",
        type: "image/jpeg",
      },
    ];
  }

  // Copy before reversing: `reverse()` mutated the caller's array and flipped it every render.
  return [...images].reverse().map((item, index) => {
    // Ensure we have a valid URL
    if (!item?.url) {
      return {
        url: `${APP_URL}/images/small-logo.png`,
        width: 800,
        height: 600,
        alt: `${index + 1}-image`,
        type: "image/jpeg",
      };
    }

    return {
      url: item.url,
      width: item.width || 800,
      height: item.height || 600,
      alt: `${index + 1}-image`,
      type: item.type || "image/jpeg",
    };
  });
};

const SEO: React.FC<ISEOProps> = ({
  title = "Travela | Short-Term Apartment & Vacation Rentals",
  description = "Book furnished apartments, vacation homes and short stays across Bangladesh with Travela.",
  keywords = [],
  canonical,
  url,
  images = [],
  robots = "index, follow, max-image-preview:large",
  jsonLd,
  siteName = "Travela",
  siteUrl = "https://travela.xyz",
  locale = "en_US",
  twitterHandle = "@travela",
  defaultKeywords = [
    "Travela",
    "Bangladesh vacation rentals",
    "short term apartment",
    "holiday homes",
  ],
  fbAppId,
  ogType = "website",
  updatedTimeISO,
  ogTitle,
  ogDescription,
  articlePublishedTime,
  articleModifiedTime,
}) => {
  const ogTitleFinal = ogTitle || title;
  const ogDescriptionFinal = ogDescription || description;
  const mergedKeywords = keywords.length > 0 ? keywords : defaultKeywords;
  const seoFormatedImages = listingImages(images);

  const canonicalUrl = makeAbsolute(canonical || url || "/", siteUrl);
  const absoluteOgUrl = makeAbsolute(url || canonicalUrl, siteUrl);

  // Primary image (WhatsApp/Facebook use the first one)
  const primary = seoFormatedImages[0]
    ? {
        ...seoFormatedImages[0],
        url: makeAbsolute(seoFormatedImages[0].url, siteUrl),
      }
    : undefined;

  // Fill mime if not provided
  if (primary && !primary.type) primary.type = inferMime(primary.url);

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  // React 19 lifts <title>, <meta> and <link> into <head> wherever they render, on the
  // server and on every client navigation; JSON-LD stays in the body, which crawlers read.
  return (
    <>
      {/* Primary */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {mergedKeywords.length > 0 && (
        <meta name="keywords" content={mergedKeywords.join(", ")} />
      )}
      <meta name="robots" content={robots} />
      <meta name="author" content={siteName} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph (Facebook/WhatsApp) */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={ogDescriptionFinal} />
      <meta property="og:url" content={absoluteOgUrl} />
      {updatedTimeISO && (
        <meta property="og:updated_time" content={updatedTimeISO} />
      )}
      {ogType === "article" && articlePublishedTime && (
        <meta
          property="article:published_time"
          content={articlePublishedTime}
        />
      )}
      {ogType === "article" && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {fbAppId && <meta property="fb:app_id" content={fbAppId} />}

      {/* Primary image for social sharing */}
      {primary && (
        <>
          <meta property="og:image" content={primary.url} />
          <meta property="og:image:secure_url" content={primary.url} />
          {primary.width && (
            <meta
              property="og:image:width"
              content={primary.width.toString()}
            />
          )}
          {primary.height && (
            <meta
              property="og:image:height"
              content={primary.height.toString()}
            />
          )}
          {primary.alt && (
            <meta property="og:image:alt" content={primary.alt} />
          )}
          {primary.type && (
            <meta property="og:image:type" content={primary.type} />
          )}
        </>
      )}

      {/* Additional images */}
      {seoFormatedImages.slice(1).map((img, i) => {
        const absoluteUrl = makeAbsolute(img.url, siteUrl);
        return (
          <React.Fragment key={i}>
            <meta property="og:image" content={absoluteUrl} />
            {img.width && (
              <meta property="og:image:width" content={img.width.toString()} />
            )}
            {img.height && (
              <meta
                property="og:image:height"
                content={img.height.toString()}
              />
            )}
            {img.alt && <meta property="og:image:alt" content={img.alt} />}
            {img.type && (
              <meta
                property="og:image:type"
                content={img.type || inferMime(absoluteUrl)}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={ogTitleFinal} />
      <meta name="twitter:description" content={ogDescriptionFinal} />
      {primary && <meta name="twitter:image" content={primary.url} />}
      {primary?.alt && <meta name="twitter:image:alt" content={primary.alt} />}

      {/* JSON-LD */}
      {jsonLdArray.map((schema, i) => (
        <script
          key={`jsonld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdService.serialize(schema) }}
        />
      ))}
    </>
  );
};

export default SEO;
